// Helper murni WILAYAH & TARIF ekspedisi manual (mig 3990).
//
// Sengaja BEBAS dari Supabase/React supaya bisa diuji apa adanya (lihat
// src/lib/__tests__/expeditionTariff.test.ts). Data-layer admin ada di
// src/lib/supabase/expeditionZones.ts; sisi server yang menghitung tarif untuk
// pembeli ada di src/lib/supabase/shippingRates.ts (`readManualRates`).
//
// ⛔ MODUL INI MENENTUKAN UANG. Dua aturan yang mengikat seluruh isinya:
//
//  1. **Tak ada angka yang dikarang.** Berat di luar tangga tarif → `null`,
//     bukan hasil ekstrapolasi dari tangga terakhir. Ongkir yang ditebak baru
//     ketahuan salah setelah nota tercetak dan barang berangkat.
//  2. **Yang paling spesifik menang**, dan aturan itu berlaku sama untuk zona
//     maupun tangga berat. Satu model mental untuk seluruh fitur: kalau
//     pemilik menambahkan aturan yang lebih sempit, ia bermaksud aturan itu
//     yang dipakai — bukan yang lebih luas yang kebetulan ditulis lebih dulu.

import { RATE_NOTE_PREFIX, type RateLeg } from "@/lib/biteship";

/** Satu aturan wilayah milik sebuah ekspedisi manual. */
export type ExpeditionZone = {
  id: string;
  expeditionId: string;
  /**
   * Nama apa adanya dari `biteship_areas`. `null` di ketiganya = ZONA SISA.
   *
   * Hierarkinya tak boleh bolong (ditegakkan CHECK mig 3990): kecamatan tanpa
   * kabupaten tak unik — ada "Garung" di Wonosobo DAN di Temanggung.
   */
  provinceName: string | null;
  cityName: string | null;
  districtName: string | null;
  /** Label siap-tampil yang dibekukan saat simpan. Hiasan, bukan kunci. */
  label: string;
  sortOrder: number;
};

/** Satu anak tangga: rentang berat → harga. */
export type RateTier = {
  id: string;
  zoneId: string;
  minWeightGram: number;
  /** `null` = tanpa batas atas. */
  maxWeightGram: number | null;
  price: number;
  sortOrder: number;
};

/** Alamat tujuan sebagaimana tersimpan — selalu berasal dari `biteship_areas`. */
export type ShipmentDestination = {
  province: string;
  city: string;
  district: string;
};

/**
 * Tarif manual yang siap ditampilkan & dipilih. Sengaja BUKAN `ShippingRate`:
 * tipe itu adalah bentuk payload cache Biteship, dan menyelundupkan tarif
 * manual ke dalamnya menuntut `courierCode` diisi sesuatu — sebuah kebohongan
 * yang akan dibaca `expeditionNameForRate` sebagai kurir sungguhan.
 */
export type ManualRate = {
  expeditionId: string;
  /** NAMA ekspedisi milik toko — inilah yang tersimpan di `shipment_requests`. */
  expeditionName: string;
  zoneId: string;
  /** Label zona yang menang, untuk ditampilkan & dicatat di catatan kirim. */
  zoneLabel: string;
  tierId: string;
  minWeightGram: number;
  maxWeightGram: number | null;
  price: number;
  /** Id kutipan `shipping_quotes` (mig 4020) — lihat `ShippingRate.quoteId`. */
  quoteId?: string;
  /** Rincian per gudang (Multigudang, mig 7200) — lihat `ShippingRate.legs`. */
  legs?: RateLeg[];
};

const norm = (v: string | null | undefined): string =>
  (v ?? "").trim().toLowerCase();

/**
 * Seberapa spesifik sebuah zona: 3 = kecamatan, 2 = kab/kota, 1 = provinsi,
 * 0 = zona sisa.
 *
 * Diturunkan dari kolom mana yang TERISI, bukan disimpan sendiri — kolom
 * turunan yang disimpan adalah kolom yang bisa berbeda dari kenyataannya.
 */
export function zoneSpecificity(zone: ExpeditionZone): 0 | 1 | 2 | 3 {
  if (norm(zone.districtName) !== "") return 3;
  if (norm(zone.cityName) !== "") return 2;
  if (norm(zone.provinceName) !== "") return 1;
  return 0;
}

/**
 * Zona ini mencakup tujuan itu?
 *
 * Kolom yang KOSONG berarti "semua" — jadi zona provinsi cocok untuk setiap
 * kab/kota di dalamnya tanpa perlu didaftarkan satu per satu. Itulah yang
 * membuat aturan tetap benar saat direktori wilayah bertambah besok.
 *
 * Perbandingannya case-insensitive & terpangkas karena keduanya teks bebas
 * dari pihak luar; yang TIDAK dilakukan adalah menebak kemiripan — "Bandung"
 * tak pernah dianggap cocok dengan "Bandung Barat".
 */
export function zoneMatches(
  zone: ExpeditionZone,
  dest: ShipmentDestination
): boolean {
  const p = norm(zone.provinceName);
  const c = norm(zone.cityName);
  const d = norm(zone.districtName);

  if (p !== "" && p !== norm(dest.province)) return false;
  if (c !== "" && c !== norm(dest.city)) return false;
  if (d !== "" && d !== norm(dest.district)) return false;
  return true;
}

/**
 * Zona yang berlaku untuk sebuah tujuan — **yang paling spesifik menang**.
 *
 * Tanpa aturan ini, "Jawa Tengah semua kabupaten" akan menelan zona "Wonosobo"
 * yang sengaja dibuat berbeda harganya, dan mana yang menang jadi bergantung
 * urutan baris di DB. Zona sisa (tingkat 0) karena itu selalu kalah dari zona
 * mana pun yang menyebut wilayah — ia jaring terakhir, bukan penimpa.
 *
 * Seri pada tingkat yang sama (mustahil lewat layar — arbiter unik mig 3990
 * menolaknya — tapi mungkin lewat SQL langsung) dipecah `sortOrder` lalu `id`,
 * supaya hasilnya tak pernah bergantung urutan baris yang kebetulan terbaca.
 */
export function matchZone(
  zones: ReadonlyArray<ExpeditionZone>,
  dest: ShipmentDestination
): ExpeditionZone | null {
  let best: ExpeditionZone | null = null;
  let bestLevel = -1;

  for (const zone of zones) {
    if (!zoneMatches(zone, dest)) continue;
    const level = zoneSpecificity(zone);
    if (level > bestLevel) {
      best = zone;
      bestLevel = level;
      continue;
    }
    if (level === bestLevel && best) {
      if (
        zone.sortOrder < best.sortOrder ||
        (zone.sortOrder === best.sortOrder && zone.id < best.id)
      ) {
        best = zone;
      }
    }
  }
  return best;
}

/** Lebar rentang sebuah tangga; tanpa batas atas = tak hingga. */
function tierSpan(tier: RateTier): number {
  return tier.maxWeightGram === null
    ? Number.POSITIVE_INFINITY
    : tier.maxWeightGram - tier.minWeightGram;
}

/** Berat ini jatuh di dalam tangga itu? Batas atas INKLUSIF. */
export function tierContains(tier: RateTier, weightGram: number): boolean {
  if (!Number.isFinite(weightGram)) return false;
  if (weightGram < tier.minWeightGram) return false;
  return tier.maxWeightGram === null || weightGram <= tier.maxWeightGram;
}

/**
 * Tangga yang berlaku untuk sebuah berat, atau `null` bila tak ada.
 *
 * `null` adalah jawaban yang SAH dan penting: berat di luar tabel berarti
 * pemilik toko belum menentukan harganya, dan satu-satunya jawaban jujur
 * adalah tidak menampilkan tarif sama sekali (pemanggil jatuh ke ongkir
 * ketik-manual). Ekstrapolasi per-kg dari tangga terakhir terasa pintar dan
 * salahnya baru ketahuan setelah barang berangkat.
 *
 * Bila beberapa tangga tumpang-tindih — keadaan yang `validateTiers`
 * peringatkan tapi tidak dilarang DB — yang menang adalah yang PALING SEMPIT,
 * cermin "paling spesifik menang" pada zona. Seri dipecah `min` terbesar, lalu
 * `sortOrder`, lalu `id`.
 */
export function tierForWeight(
  tiers: ReadonlyArray<RateTier>,
  weightGram: number
): RateTier | null {
  let best: RateTier | null = null;

  for (const tier of tiers) {
    if (!tierContains(tier, weightGram)) continue;
    if (!best) {
      best = tier;
      continue;
    }
    const span = tierSpan(tier);
    const bestSpan = tierSpan(best);
    if (span < bestSpan) {
      best = tier;
    } else if (span === bestSpan) {
      if (
        tier.minWeightGram > best.minWeightGram ||
        (tier.minWeightGram === best.minWeightGram &&
          (tier.sortOrder < best.sortOrder ||
            (tier.sortOrder === best.sortOrder && tier.id < best.id)))
      ) {
        best = tier;
      }
    }
  }
  return best;
}

/**
 * SEMUA zona yang mencakup sebuah tujuan, urut dari yang paling spesifik.
 *
 * `matchZone` memulangkan pemenangnya saja; ini memulangkan seluruh peserta.
 * Dipakai alat "Uji zona" di layar admin untuk menunjukkan zona mana yang
 * KALAH — tanpa itu, aturan "paling spesifik menang" tak terlihat dari daftar
 * mana pun, dan pemilik baru sadar zonanya salah saat nota sudah tercetak.
 */
export function matchingZones(
  zones: ReadonlyArray<ExpeditionZone>,
  dest: ShipmentDestination
): ExpeditionZone[] {
  return zones
    .filter((z) => zoneMatches(z, dest))
    .sort(
      (a, b) =>
        zoneSpecificity(b) - zoneSpecificity(a) ||
        a.sortOrder - b.sortOrder ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
    );
}

/**
 * Kenapa sebuah tujuan + berat menghasilkan tarif — atau kenapa TIDAK.
 *
 * `manualRateFor` cukup menjawab "ada angka atau tidak"; alat uji di layar
 * admin butuh SEBABNYA, karena ketiga sebab kegagalannya menuntut tindakan
 * yang berbeda: menambah zona, mengisi tangga, atau memperlebar rentang berat.
 * Menyamakan ketiganya jadi "tak ada tarif" memaksa pemilik menebak yang mana.
 *
 * Keduanya berbagi SATU jalur keputusan — `manualRateFor` memanggil fungsi ini
 * — supaya yang dijelaskan alat uji dijamin sama dengan yang benar-benar
 * dipakai saat kiriman dibuat. Alat uji yang punya logikanya sendiri adalah
 * alat uji yang bisa berbohong.
 */
export type ManualRateExplanation =
  | {
      kind: "ok";
      zone: ExpeditionZone;
      tier: RateTier;
      rate: ManualRate;
      /** Zona lain yang juga cocok tapi KALAH karena kurang spesifik. */
      alsoMatched: ExpeditionZone[];
    }
  | { kind: "no-zone" }
  | { kind: "no-tiers"; zone: ExpeditionZone; alsoMatched: ExpeditionZone[] }
  | {
      kind: "weight-outside";
      zone: ExpeditionZone;
      /** Tangga milik zona pemenang, untuk ditunjukkan apa adanya. */
      tiers: RateTier[];
      alsoMatched: ExpeditionZone[];
    };

export function explainManualRate(args: {
  expeditionId: string;
  expeditionName: string;
  zones: ReadonlyArray<ExpeditionZone>;
  tiers: ReadonlyArray<RateTier>;
  destination: ShipmentDestination;
  weightGram: number;
}): ManualRateExplanation {
  const matched = matchingZones(args.zones, args.destination);
  const zone = matched[0];
  if (!zone) return { kind: "no-zone" };

  const alsoMatched = matched.slice(1);
  const zoneTiers = args.tiers
    .filter((t) => t.zoneId === zone.id)
    .sort((a, b) => a.minWeightGram - b.minWeightGram);

  if (zoneTiers.length === 0) {
    return { kind: "no-tiers", zone, alsoMatched };
  }

  const tier = tierForWeight(zoneTiers, args.weightGram);
  if (!tier) {
    return { kind: "weight-outside", zone, tiers: zoneTiers, alsoMatched };
  }

  return {
    kind: "ok",
    zone,
    tier,
    alsoMatched,
    rate: {
      expeditionId: args.expeditionId,
      expeditionName: args.expeditionName,
      zoneId: zone.id,
      zoneLabel: zone.label.trim() || zoneLabel(zone),
      tierId: tier.id,
      minWeightGram: tier.minWeightGram,
      maxWeightGram: tier.maxWeightGram,
      price: tier.price,
    },
  };
}

/**
 * Tarif manual satu ekspedisi untuk sebuah tujuan + berat, atau `null`.
 *
 * `null` punya tiga sebab yang semuanya sah dan tak dibedakan di sini: tak ada
 * zona yang mencakup tujuan, zona ada tapi tangganya kosong, atau beratnya di
 * luar tangga. Pemanggil di jalur kiriman hanya perlu tahu "tak ada angka";
 * yang MEMBEDAKAN ketiganya adalah `explainManualRate`, dipakai alat uji di
 * layar admin tempat perbedaan itu bisa ditindaklanjuti.
 */
export function manualRateFor(args: {
  expeditionId: string;
  expeditionName: string;
  zones: ReadonlyArray<ExpeditionZone>;
  tiers: ReadonlyArray<RateTier>;
  destination: ShipmentDestination;
  weightGram: number;
}): ManualRate | null {
  const result = explainManualRate(args);
  return result.kind === "ok" ? result.rate : null;
}

// ─── Ekspedisi manual TANPA harga (2026-11-05) ─────────────────────────────

/**
 * Ekspedisi manual yang tetap DITAWARKAN walau tak punya angka — kartu
 * "Ongkir ditentukan admin". Memilihnya tak membawa kutipan apa pun, jadi
 * ongkirnya ditetapkan admin lewat Set Ongkir, persis jalur pemilih cadangan.
 *
 * `reason`:
 *  • `no-zones` — ekspedisi belum punya zona sama sekali. Default-nya (putusan
 *    pemilik 2026-11-05) = SEMUA WILAYAH: ekspedisi manual yang baru dibuat
 *    harus langsung bisa dipilih, bukan menunggu pemilik menyusun zona dulu.
 *  • `no-tiers` — zona yang menang untuk tujuan ini ada, tapi tangga tarifnya
 *    belum diisi.
 */
export type UnpricedManualExpedition = {
  expeditionId: string;
  expeditionName: string;
  reason: "no-zones" | "no-tiers";
};

export type ManualExpeditionOffer =
  | { kind: "priced"; rate: ManualRate }
  | { kind: "unpriced"; expedition: UnpricedManualExpedition }
  | { kind: "hidden" };

/**
 * Apa yang ditawarkan SATU ekspedisi manual untuk sebuah tujuan + berat.
 *
 * ⛔ Yang TETAP disembunyikan, dan alasannya:
 *  • zonanya ada tapi tak satu pun mencakup tujuan (`no-zone`) — pemilik
 *    sengaja membatasi wilayah layanannya (JNT FLAT 7.000 Galea hanya 22
 *    kab./kota). Menawarkannya di luar itu membatalkan pembatasan tersebut;
 *  • berat di luar tangga (`weight-outside`) — pemilik sudah menyusun tangga
 *    dan berat ini sengaja tak dihargai. Itu keputusan, bukan kelupaan.
 *
 * Tak ada angka yang dikarang di cabang mana pun: `unpriced` tak membawa harga.
 * Jalur berharga tetap lewat `explainManualRate` — SATU jalur keputusan yang
 * sama dengan alat "Uji zona", jadi kartu berharga di sini mustahil berbeda
 * dari yang dijelaskan alat uji.
 */
export function manualExpeditionOffer(args: {
  expeditionId: string;
  expeditionName: string;
  zones: ReadonlyArray<ExpeditionZone>;
  tiers: ReadonlyArray<RateTier>;
  destination: ShipmentDestination;
  weightGram: number;
}): ManualExpeditionOffer {
  const unpriced = (reason: UnpricedManualExpedition["reason"]) => ({
    kind: "unpriced" as const,
    expedition: {
      expeditionId: args.expeditionId,
      expeditionName: args.expeditionName,
      reason,
    },
  });

  if (args.zones.length === 0) return unpriced("no-zones");

  const result = explainManualRate(args);
  switch (result.kind) {
    case "ok":
      return { kind: "priced", rate: result.rate };
    case "no-tiers":
      return unpriced("no-tiers");
    default:
      return { kind: "hidden" };
  }
}

/**
 * Seluruh ekspedisi manual aktif satu toko → kartu berharga + kartu tanpa
 * harga, masing-masing dalam urutan yang STABIL.
 *
 * Berharga: harga naik lalu nama (cermin `normalizeRates`). Tanpa harga: nama.
 * Urutan yang bergantung urutan baris DB membuat dua permintaan identik
 * menghasilkan dua daftar berbeda.
 */
export function buildManualOffers(args: {
  expeditions: ReadonlyArray<{ id: string; name: string }>;
  zones: ReadonlyArray<ExpeditionZone>;
  tiers: ReadonlyArray<RateTier>;
  destination: ShipmentDestination;
  weightGram: number;
}): { rates: ManualRate[]; unpriced: UnpricedManualExpedition[] } {
  const rates: ManualRate[] = [];
  const unpriced: UnpricedManualExpedition[] = [];

  for (const exp of args.expeditions) {
    const offer = manualExpeditionOffer({
      expeditionId: exp.id,
      expeditionName: exp.name,
      zones: args.zones.filter((z) => z.expeditionId === exp.id),
      tiers: args.tiers,
      destination: args.destination,
      weightGram: args.weightGram,
    });
    if (offer.kind === "priced") rates.push(offer.rate);
    else if (offer.kind === "unpriced") unpriced.push(offer.expedition);
  }

  rates.sort(
    (a, b) =>
      a.price - b.price || a.expeditionName.localeCompare(b.expeditionName, "id")
  );
  unpriced.sort((a, b) => a.expeditionName.localeCompare(b.expeditionName, "id"));
  return { rates, unpriced };
}

/** Kunci stabil satu kartu tanpa harga — tak pernah bentrok dengan `manualRateKey`. */
export function unpricedManualKey(exp: UnpricedManualExpedition): string {
  return `manual-unpriced:${exp.expeditionId}`;
}

/**
 * Satu baris catatan jejak untuk tarif manual — awalan SAMA dengan jalur
 * Biteship (`RATE_NOTE_PREFIX`) supaya berganti antar-sumber MENIMPA barisnya,
 * bukan menumpuk dua baris yang saling bertentangan.
 *
 * Zona ikut ditulis, bukan hanya harga: saat ongkir yang ditagihkan tak cocok
 * dengan yang diperkirakan, pertanyaan pertamanya selalu "zona mana yang
 * dipakai" — dan itu satu-satunya bagian yang tak bisa ditebak ulang dari
 * angka.
 */
export function manualRateNoteText(rate: ManualRate): string {
  return [
    `${RATE_NOTE_PREFIX} ${rate.expeditionName}`,
    rate.zoneLabel,
    tierRangeLabel({
      id: rate.tierId,
      zoneId: rate.zoneId,
      minWeightGram: rate.minWeightGram,
      maxWeightGram: rate.maxWeightGram,
      price: rate.price,
      sortOrder: 0,
    }),
    `Rp ${Math.round(rate.price).toLocaleString("id-ID")}`,
  ]
    .filter((part) => part.trim() !== "")
    .join(" · ");
}

/** Kunci stabil satu tarif manual — cermin `rateKey` di jalur Biteship. */
export function manualRateKey(rate: ManualRate): string {
  return `manual:${rate.expeditionId}:${rate.zoneId}:${rate.tierId}`;
}

// ─── Label ──────────────────────────────────────────────────────────────────

/**
 * Label zona yang terbaca manusia, dari yang paling spesifik ke yang umum.
 *
 * ⚠️ "Kab./Kota", bukan "Kab." — Biteship TIDAK membedakan kabupaten dan kota
 * (`city_name`), dan satu nilai bisa memuat keduanya sekaligus (temuan data di
 * `regionLabel.ts`: "Bandung" memuat kecamatan Kota Bandung DAN Kabupaten
 * Bandung). Menulis "Kab. Bandung" pada zona yang juga mencakup Kota Bandung
 * adalah fakta yang dikarang.
 */
export function zoneLabel(zone: ExpeditionZone): string {
  const province = (zone.provinceName ?? "").trim();
  const city = (zone.cityName ?? "").trim();
  const district = (zone.districtName ?? "").trim();

  // "Semua wilayah", bukan lagi "Semua wilayah lain" (2026-11-05): kata
  // "lain" membuat pilihan ini terbaca sebagai cadangan, padahal pada
  // ekspedisi tanpa zona lain ia memang berlaku untuk seluruh Indonesia.
  // Label beku zona lama tetap apa adanya — `label` hiasan, bukan kunci.
  if (province === "") return "Semua wilayah";
  if (city === "") return `${province} · semua kab./kota`;
  if (district === "") return `${city}, ${province} · semua kecamatan`;
  return `${district}, ${city}, ${province}`;
}

// ─── Pemilih wilayah form "Zona Baru" ──────────────────────────────────────

/**
 * Nilai dropdown Provinsi untuk "Semua wilayah (seluruh Indonesia)" — zona
 * dengan ketiga kolom kosong (zona sisa, tingkat 0).
 *
 * Sentinel, bukan `""`: string kosong sudah berarti "belum memilih provinsi",
 * dan menyamakan keduanya membuat form yang belum disentuh tersimpan sebagai
 * zona seluruh Indonesia.
 */
export const ALL_REGIONS_VALUE = "__semua_wilayah__";

/**
 * Pilihan dropdown → isi zona yang disimpan. `null` = belum bisa disimpan
 * (provinsi belum dipilih).
 *
 * "Semua wilayah" MENGOSONGKAN dua tingkat di bawahnya apa pun isinya: kab./
 * kota yang tertinggal dari pilihan sebelumnya akan menghasilkan zona yang
 * hierarkinya bolong (ditolak CHECK mig 3990) — atau lebih buruk, zona sempit
 * yang dikira seluruh Indonesia.
 */
export function zoneDraftFromPicker(picked: {
  province: string;
  city: string;
  district: string;
}): {
  provinceName: string | null;
  cityName: string | null;
  districtName: string | null;
} | null {
  if (picked.province === ALL_REGIONS_VALUE) {
    return { provinceName: null, cityName: null, districtName: null };
  }
  const province = picked.province.trim();
  if (province === "") return null;
  const city = picked.city.trim();
  const district = city === "" ? "" : picked.district.trim();
  return {
    provinceName: province,
    cityName: city || null,
    districtName: district || null,
  };
}

/**
 * Pilihan awal dropdown Provinsi saat form "Zona Baru" dibuka.
 *
 * Bawaannya "Semua wilayah" (putusan pemilik 2026-11-05) — KECUALI ekspedisi
 * ini sudah punya zona semua wilayah: menawarkannya lagi sebagai bawaan hanya
 * berakhir di penolakan "zona untuk wilayah itu sudah ada" saat disimpan.
 */
export function defaultZonePickerProvince(
  zones: ReadonlyArray<ExpeditionZone>
): string {
  return zones.some((z) => zoneSpecificity(z) === 0) ? "" : ALL_REGIONS_VALUE;
}

/** "300 g – 1 kg", "1 kg ke atas", "0 – 300 g". */
export function tierRangeLabel(tier: RateTier): string {
  const from = formatGram(tier.minWeightGram);
  if (tier.maxWeightGram === null) return `${from} ke atas`;
  return `${from} – ${formatGram(tier.maxWeightGram)}`;
}

function formatGram(gram: number): string {
  if (gram >= 1000 && gram % 1000 === 0) {
    return `${(gram / 1000).toLocaleString("id-ID")} kg`;
  }
  return `${Math.round(gram).toLocaleString("id-ID")} g`;
}

// ─── Validasi tangga tarif ──────────────────────────────────────────────────

export type TierProblem =
  | { kind: "overlap"; aIndex: number; bIndex: number; message: string }
  | { kind: "gap"; afterIndex: number; fromGram: number; toGram: number; message: string }
  | { kind: "invalid"; index: number; message: string };

/**
 * Periksa satu tangga tarif — dipakai layar admin untuk memperingatkan SEBELUM
 * disimpan.
 *
 * Ketiga cacatnya gagal SENYAP kalau tak diperiksa, dan itulah alasan fungsi
 * ini ada:
 *   • **tumpang-tindih** → harga bergantung aturan tie-break yang tak terlihat
 *     pemilik; dua baris yang sama-sama "benar" memberi dua harga berbeda;
 *   • **celah** → paket 1.001 g tiba-tiba tak punya ongkir sama sekali,
 *     padahal 1.000 g dan 1.500 g punya;
 *   • **rentang terbalik** (min > max) → tangga yang tak pernah cocok apa pun.
 *
 * Peringatan, BUKAN penolakan: pemilik yang sedang menyusun ulang tangganya
 * berhak melewati keadaan setengah jadi. Yang tak boleh adalah ia tak diberi
 * tahu.
 */
export function validateTiers(
  tiers: ReadonlyArray<RateTier>
): TierProblem[] {
  const problems: TierProblem[] = [];

  tiers.forEach((tier, index) => {
    if (!Number.isFinite(tier.minWeightGram) || tier.minWeightGram < 0) {
      problems.push({
        kind: "invalid",
        index,
        message: "Berat minimum harus 0 atau lebih.",
      });
    }
    if (tier.maxWeightGram !== null && tier.maxWeightGram < tier.minWeightGram) {
      problems.push({
        kind: "invalid",
        index,
        message: "Berat maksimum lebih kecil dari minimum — tangga ini tak akan pernah dipakai.",
      });
    }
    if (!Number.isFinite(tier.price) || tier.price < 0) {
      problems.push({
        kind: "invalid",
        index,
        message: "Harga tidak boleh negatif.",
      });
    }
  });

  // Urut menaik dulu supaya celah & tumpang-tindih bisa dibaca berurutan;
  // indeks ASLI dibawa serta karena pesan error menunjuk baris di layar.
  const sorted = tiers
    .map((tier, index) => ({ tier, index }))
    .filter(({ tier }) => Number.isFinite(tier.minWeightGram))
    .sort(
      (a, b) =>
        a.tier.minWeightGram - b.tier.minWeightGram ||
        tierSpan(a.tier) - tierSpan(b.tier)
    );

  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    const prevMax = prev.tier.maxWeightGram;

    if (prevMax === null) {
      problems.push({
        kind: "overlap",
        aIndex: prev.index,
        bIndex: cur.index,
        message: `Tangga "${tierRangeLabel(prev.tier)}" tanpa batas atas menutupi tangga di bawahnya.`,
      });
      continue;
    }

    if (cur.tier.minWeightGram <= prevMax) {
      problems.push({
        kind: "overlap",
        aIndex: prev.index,
        bIndex: cur.index,
        message: `"${tierRangeLabel(prev.tier)}" dan "${tierRangeLabel(cur.tier)}" beririsan.`,
      });
    } else if (cur.tier.minWeightGram > prevMax + 1) {
      problems.push({
        kind: "gap",
        afterIndex: prev.index,
        fromGram: prevMax + 1,
        toGram: cur.tier.minWeightGram - 1,
        message: `Berat ${formatGram(prevMax + 1)}–${formatGram(cur.tier.minWeightGram - 1)} tidak punya tarif.`,
      });
    }
  }

  return problems;
}

/**
 * Saran nilai `min` untuk baris tarif BARU: tepat di atas batas tertinggi yang
 * sudah ada.
 *
 * Ini pencegahan celah yang paling murah — celah hampir selalu lahir dari
 * mengetik ulang angka yang seharusnya berurutan, bukan dari niat. Tangga
 * tanpa batas atas mengembalikan `null`: tak ada "di atas tak hingga", dan
 * layar memakai itu untuk mematikan tombol "tambah tangga".
 */
export function nextTierMinGram(
  tiers: ReadonlyArray<RateTier>
): number | null {
  if (tiers.length === 0) return 0;
  if (tiers.some((t) => t.maxWeightGram === null)) return null;
  const highest = Math.max(
    ...tiers.map((t) => t.maxWeightGram ?? t.minWeightGram)
  );
  return highest + 1;
}
